import { useState } from 'react';
import { log } from '../../app/logger';
import { deletePlayer, upsertPlayer } from '../../app/roster';
import type { RosterPlayerInput } from '../../app/roster';
import { saveTeamShape } from '../../app/settingsB';
import type { DispatchResult } from '../../app/bus';
import { freshnessLabel } from '../freshness';
import { ATTRIBUTES } from '../../types';
import type { OurPlayerRow, TeamShape } from '../../types';
import { PlayerSheet } from './PlayerSheet';
import { ShapeSheet } from './ShapeSheet';
import { useRoster } from './useRoster';

/**
 * B spec §2.4 / §17 — **the My Team tab** (build units B-6, B-28; scenario 2): the team shape and
 * our squad, over B-2's facade and B-26's settings facade.
 *
 * ── An out-of-match tab, optimised for review rather than for entry ─────────
 *
 * No in-play state can render this, so typing is legal and the no-required-typing rule has nothing
 * to say. §17 is explicit that there is **no first-run experience, no onboarding wizard, no guided
 * rating flow and no completeness nag** — the stakeholder maintains 12–15 players at each practice,
 * so the screen answers *"is this profile still true?"*, which is what actually happens before a
 * season, after a position change, and after a match.
 *
 * ── Refusals are ordinary sentences ────────────────────────────────────────
 *
 * A `DispatchResult` rejection here is an ordinary event, not a fault: `ERR_ROSTER_NOT_FOUND` is a
 * stale screen deleting an already-deleted row, and the engine's message is already the sentence
 * the user should read. So every rejection renders through the same quiet `role="status"` line the
 * success notices use — never the fatal banner — and is followed by a re-read, which is what
 * actually repairs the stale screen.
 *
 * ── The toggle writes the whole row through the one command ────────────────
 *
 * There is no "set active" command and should not be: `UpsertOurPlayer`'s edit arm narrows to the
 * editable field union, so the toggle dispatches the row it is looking at with `active` flipped.
 */

const MODULE = 'ui-b/myteam';

type SheetState =
  | { readonly kind: 'closed' }
  | { readonly kind: 'add' }
  | { readonly kind: 'edit'; readonly player: OurPlayerRow }
  | { readonly kind: 'shape' };

export function MyTeamScreen() {
  const { state, actions } = useRoster();
  const [sheet, setSheet] = useState<SheetState>({ kind: 'closed' });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  /** One write path: dispatch → sentence (on refusal) → re-read → close (on success). */
  const run = (work: Promise<DispatchResult>, doneNotice: string | null): void => {
    setBusy(true);
    setProblem(null);
    void work
      .then((result) => {
        if (!result.ok) {
          if (sheet.kind === 'closed') setNotice(result.message);
          else setProblem(result.message);
          /* A refusal can mean the screen is stale (the not-found case) — re-read either way. */
          actions.refresh();
          return;
        }
        actions.refresh();
        setSheet({ kind: 'closed' });
        setNotice(doneNotice);
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'B §2.4: a roster write failed', {
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setProblem('That could not be saved. The log has the detail.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const save = (input: RosterPlayerInput): void => {
    run(upsertPlayer(input), null);
  };

  const remove = (playerId: string): void => {
    run(deletePlayer(playerId), 'Removed from the roster.');
  };

  const saveShape = (shape: Omit<TeamShape, 'updatedAt'>): void => {
    run(saveTeamShape(shape), 'Team shape saved.');
  };

  const toggle = (player: OurPlayerRow): void => {
    run(
      upsertPlayer({
        id: player.id,
        jerseyNumber: player.jerseyNumber,
        firstName: player.firstName,
        primaryPosition: player.primaryPosition,
        secondaryPositions: player.secondaryPositions,
        ratings: player.ratings,
        active: !player.active,
      }),
      null,
    );
  };

  if (state.phase === 'loading') return <p className="homeb-empty">Reading your roster…</p>;
  if (state.phase === 'failed')
    return (
      <p className="homeb-empty" role="alert">
        Your roster could not be read. The log has the detail.
      </p>
    );

  const active = state.players.filter((player) => player.active);
  const inactive = state.players.filter((player) => !player.active);
  // B-1: `now` decides whether the stamp prints its year, so the clock is read here rather than
  // hidden inside the label. A profile last changed in another calendar year renders "4 Sep 2025".
  const stamp = freshnessLabel(state.facts.lastChanged, Date.now());

  return (
    <div
      className="myteam"
      data-testid="myteam"
      data-read={String(state.reads)}
      data-players={String(state.players.length)}
      data-active={String(active.length)}
    >
      <h1 className="myteam__title">My Team</h1>
      {/* §2.4 — "a one-line explanation that Coach Claude reads this roster." Kept verbatim. */}
      <p className="myteam__why" data-testid="myteam-why">
        Coach Claude reads this roster when it writes suggestions for your team.
      </p>
      {/* Pre-mortem PM-1 — quiet, never a warning, never a gate. Covers the shape as well as the
          roster, which is what ruling R-3's `updatedAt`-inside-the-value bought. */}
      {stamp !== null && (
        <p className="myteam__freshness" data-testid="myteam-freshness">
          Last changed {stamp}.
        </p>
      )}

      {notice !== null && (
        <p className="myteam-notice" data-testid="myteam-notice" role="status">
          {notice}
          <button
            type="button"
            className="myteam-notice__dismiss"
            onClick={() => {
              setNotice(null);
            }}
          >
            Dismiss
          </button>
        </p>
      )}

      {/* ── The team shape card, above the squad ──────────────────────────── */}
      <ShapeCard
        shape={state.shape}
        onOpen={() => {
          setProblem(null);
          setNotice(null);
          setSheet({ kind: 'shape' });
        }}
      />

      <button
        type="button"
        className="myteam-add"
        data-testid="myteam-add"
        onClick={() => {
          setProblem(null);
          setNotice(null);
          setSheet({ kind: 'add' });
        }}
      >
        Add a player
      </button>

      {state.players.length === 0 ? (
        <p className="homeb-empty" data-testid="myteam-empty">
          No players yet. Add your squad once — every match after that reuses it.
          {/* §15.5 — the one added line for a user whose free-text rows v005 cleared. */}
          <span className="myteam-empty__migration" data-testid="myteam-empty-migration">
            {' '}
            The roster format changed — players are now a number, a name, positions and ratings.
          </span>
        </p>
      ) : (
        <ul className="myteam-list" data-testid="myteam-list">
          {active.map((player) => (
            <PlayerRow
              key={player.id}
              player={player}
              busy={busy}
              onOpen={() => {
                setProblem(null);
                setNotice(null);
                setSheet({ kind: 'edit', player });
              }}
              onToggle={() => {
                toggle(player);
              }}
            />
          ))}
        </ul>
      )}

      {/* ── Not available — the collapsed footer section ───────────────────── */}
      {inactive.length > 0 && (
        <section className="myteam-inactive" data-testid="myteam-inactive">
          <button
            type="button"
            className="myteam-disclosure__header"
            data-testid="myteam-inactive-header"
            aria-expanded={showInactive}
            onClick={() => {
              setShowInactive((open) => !open);
            }}
          >
            {/* The header states the CONSEQUENCE, not just the count — §17.1. */}
            Not available ({inactive.length}) — not sent to Coach Claude
          </button>
          <div className="myteam-disclosure__body" data-open={showInactive ? 'true' : 'false'}>
            <ul className="myteam-list myteam-list--inactive">
              {inactive.map((player) => (
                <PlayerRow
                  key={player.id}
                  player={player}
                  busy={busy}
                  onOpen={() => {
                    setProblem(null);
                    setNotice(null);
                    setSheet({ kind: 'edit', player });
                  }}
                  onToggle={() => {
                    toggle(player);
                  }}
                />
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ── What Coach Claude reads — the actual rendered prose ────────────── */}
      <section className="myteam-preview" data-testid="myteam-preview">
        <button
          type="button"
          className="myteam-disclosure__header"
          data-testid="myteam-preview-header"
          aria-expanded={showPreview}
          onClick={() => {
            setShowPreview((open) => !open);
          }}
        >
          What Coach Claude reads
        </button>
        <div className="myteam-disclosure__body" data-open={showPreview ? 'true' : 'false'}>
          {/* Produced by calling the SAME `renderTeamProfile` the payload calls, so the preview
              cannot drift from what leaves the device — §5.6's privacy story made concrete rather
              than promised. Read-only by construction: it is a `<pre>`, not a field. */}
          <pre className="myteam-preview__text" data-testid="myteam-preview-text">
            {state.preview ?? 'Nothing yet — add a player or record your shape.'}
          </pre>
        </div>
      </section>

      {(sheet.kind === 'add' || sheet.kind === 'edit') && (
        <PlayerSheet
          player={sheet.kind === 'edit' ? sheet.player : null}
          squad={state.players}
          busy={busy}
          problem={problem}
          onSave={save}
          onDelete={remove}
          onClose={() => {
            setSheet({ kind: 'closed' });
            setProblem(null);
          }}
        />
      )}

      {sheet.kind === 'shape' && (
        <ShapeSheet
          shape={state.shape}
          busy={busy}
          problem={problem}
          onSave={saveShape}
          onClose={() => {
            setSheet({ kind: 'closed' });
            setProblem(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * §17.1 — the shape card, **above the squad**, because there is exactly one of it and a fourteen-
 * row list below would bury it.
 */
function ShapeCard({ shape, onOpen }: { readonly shape: TeamShape; readonly onOpen: () => void }) {
  const rows: readonly (readonly [string, string])[] = [
    ['Out of possession', shape.outOfPossession],
    ['In possession', shape.inPossession],
    ['Between the two', shape.transition],
  ];
  const anything = rows.some(([, value]) => value.trim() !== '');

  return (
    <button
      type="button"
      className="myteam-shape"
      data-testid="myteam-shape-card"
      data-set={anything ? 'true' : 'false'}
      onClick={onOpen}
    >
      <span className="myteam-shape__title">Team shape</span>
      {anything ? (
        <span className="myteam-shape__rows">
          {rows.map(([label, value]) => (
            <span className="myteam-shape__row" key={label}>
              <span className="myteam-shape__key">{label}</span>
              <span className="myteam-shape__value" data-empty={value.trim() === ''}>
                {value.trim() === '' ? 'Not set' : value}
              </span>
            </span>
          ))}
        </span>
      ) : (
        <span className="myteam-shape__prompt" data-testid="myteam-shape-empty">
          No shape recorded — Coach Claude will work from the roster alone.
        </span>
      )}
    </button>
  );
}

/**
 * §17.1's list row. Three facts and no more: who, where they can play, and how much of them has
 * been assessed.
 *
 * The **`fixed` glyph** is the one safety-relevant fact the list surfaces, so the user can see at
 * a glance which players are immovable — never a blank, never hue-only.
 *
 * The rating summary is `6 strong` / `3 to work on`. **A zero never renders**, and a wholly
 * unrated player reads *not assessed yet* — the requirement's own words, so the screen never
 * asserts a judgement about a blank any more than the payload does. `okay` is deliberately not
 * summarised; it earns no pixels. There is **no completeness meter**: a progress bar over 19
 * optional fields invites completionism, and §3.7.37 says most records are partly filled *by
 * design*.
 */
function PlayerRow({
  player,
  busy,
  onOpen,
  onToggle,
}: {
  readonly player: OurPlayerRow;
  readonly busy: boolean;
  readonly onOpen: () => void;
  readonly onToggle: () => void;
}) {
  let strong = 0;
  let toWorkOn = 0;
  let rated = 0;
  for (const attribute of ATTRIBUTES) {
    const rating = player.ratings[attribute.key];
    if (rating === undefined) continue;
    rated += 1;
    if (rating === 'great') strong += 1;
    if (rating === 'needsWork') toWorkOn += 1;
  }

  return (
    <li className="myteam-row" data-active={player.active ? 'true' : 'false'}>
      <button
        type="button"
        className="myteam-row__open"
        data-testid={`myteam-player-${player.id}`}
        data-fixed={player.secondaryPositions.length === 0 ? 'true' : 'false'}
        onClick={onOpen}
      >
        <span className="myteam-row__id">
          <span className="myteam-row__jersey">{player.jerseyNumber}</span>
          <span className="myteam-row__name">{player.firstName}</span>
        </span>
        <span className="myteam-row__meta">
        <span className="myteam-row__positions">
          <span className="myteam-pos myteam-pos--primary">{player.primaryPosition}</span>
          {player.secondaryPositions.length === 0 ? (
            <span className="myteam-pos myteam-pos--fixed" data-testid={`myteam-fixed-${player.id}`}>
              <span aria-hidden="true">🔒</span> fixed
            </span>
          ) : (
            player.secondaryPositions.map((position) => (
              <span className="myteam-pos" key={position}>
                {position}
              </span>
            ))
          )}
        </span>
        <span className="myteam-row__ratings" data-testid={`myteam-summary-${player.id}`}>
          {rated === 0 ? (
            <span className="myteam-row__unrated">not assessed yet</span>
          ) : (
            <>
              {strong > 0 && <span className="myteam-row__strong">{strong} strong</span>}
              {toWorkOn > 0 && (
                <span className="myteam-row__towork">{toWorkOn} to work on</span>
              )}
            </>
          )}
        </span>
        </span>
      </button>
      {/* §2.4's active toggle. State is never hue-only: the word changes with it. */}
      <button
        type="button"
        className="myteam-row__toggle"
        data-testid={`myteam-toggle-${player.id}`}
        role="switch"
        aria-checked={player.active}
        disabled={busy}
        onClick={onToggle}
      >
        {player.active ? 'Active' : 'Inactive'}
      </button>
    </li>
  );
}
